import { useState } from "react";
import { Check, Download, Loader2, RefreshCw } from "lucide-react";
import { useToast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";
import { downloadChapter } from "../../lib/downloadChapter";

type Props = {
  chapterId: string;
  chapterTitle: string;
  /** Version of the chapter that is live now. */
  version: number;
  /** Version saved on this device, or null when it is not saved. */
  savedVersion: number | null;
  token: string;
  userId: string | null;
  onSaved: () => void;
};

const BASE = "flex h-11 w-11 shrink-0 items-center justify-center rounded-control hover:bg-lift focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Three states: not downloaded, downloading with progress, downloaded. A newer live version shows an update icon. */
export function ChapterDownloadButton({ chapterId, chapterTitle, version, savedVersion, token, userId, onSaved }: Props) {
  const { showToast } = useToast();
  const [percent, setPercent] = useState<number | null>(null);

  if (!userId) return null;

  if (percent !== null) {
    return (
      <span role="status" className={`${BASE} text-xs font-semibold text-primary`} aria-label={`Downloading ${chapterTitle}, ${percent} percent`}>
        {percent < 100 ? <><Loader2 size={14} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />{percent}</> : <Check size={18} aria-hidden="true" />}
      </span>
    );
  }

  if (savedVersion !== null && savedVersion >= version) {
    return (
      <span role="img" aria-label={`${chapterTitle} is downloaded`} className={`${BASE} text-success`}>
        <Check size={18} aria-hidden="true" />
      </span>
    );
  }

  const run = async () => {
    setPercent(0);
    try {
      await downloadChapter(token, userId, chapterId, setPercent);
      showToast("success", "Saved for offline reading");
      onSaved();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setPercent(null);
    }
  };

  const update = savedVersion !== null;
  return (
    <button type="button" onClick={() => void run()} aria-label={update ? `Update the downloaded copy of ${chapterTitle}` : `Download ${chapterTitle}`} className={`${BASE} text-muted-foreground`}>
      {update ? <RefreshCw size={18} aria-hidden="true" /> : <Download size={18} aria-hidden="true" />}
    </button>
  );
}
